import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useUpdateSmartAccountSigners } from '~/hooks/merkle-transactions/useUpdateSmartAccountSigners'
import { useActiveOrgId } from '~/hooks/org'
import { OrgSigner, OrgSmartAccount } from '~/types'
import { shortenAddress } from '~/utils/addresses'

// `save` resolves undefined on failure so dialogs can stay open with edits intact
export const useSaveSmartAccountSigners = ({
  smartAccount,
  isRecoveryTx,
}: {
  smartAccount: OrgSmartAccount
  isRecoveryTx: boolean
}) => {
  const { createUpdateSignersTransaction, isProcessing } =
    useUpdateSmartAccountSigners()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const save = useCallback(
    async ({
      signers,
      threshold,
    }: {
      signers: OrgSigner[]
      threshold: number
    }) => {
      return await createUpdateSignersTransaction({
        smartAccountId: smartAccount.id,
        smartAccountAddress: smartAccount.address,
        newAuthenticatorIds: signers.map((signer) => signer.id),
        newThreshold: threshold,
        isRecoveryTx,
        ...(isRecoveryTx && {
          memo: `Reset signers on ${smartAccount.name} (${shortenAddress(
            smartAccount.address,
          )})`,
        }),
        onConfirmation: () => {
          queryClient.setQueryData(
            [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
            { ...smartAccount, signers, threshold },
          )
        },
      })
    },
    [
      createUpdateSignersTransaction,
      smartAccount,
      isRecoveryTx,
      queryClient,
      activeOrgId,
    ],
  )

  return { save, isProcessing }
}
